<template>
  <el-drawer
      title="编辑信息"
      :visible.sync="visible"
      direction="rtl"
      size="700"
      :before-close="handleClose"
      :z-index="999"
  >
    <!-- 隐藏的上传组件 -->
    <el-upload
        v-if="!isDestroyed"
        ref="uploadRef"
        style="display: none"
        class="uploadFileBox"
        action="#"
        :multiple="false"
        :http-request="handleFileUpload"
    ></el-upload>

    <!-- 插入链接弹窗 -->
    <insert-link
        v-if="insertLinkShow"
        @confirm="insertLink"
        :visible.sync="insertLinkShow"
        :insertLinkName="insertLinkName"
    />

    <!-- 编辑表单 -->
    <div class="form-box">
      <el-form
          :model="editForm"
          :rules="rules"
          ref="editFormRef"
          label-position="left"
          label-width="100px"
          @submit.native.prevent
      >
        <el-form-item label="标题" prop="title">
          <el-col :span="6">
            <el-input
                v-model="editForm.title"
                clearable
                placeholder="请输入标题"
            ></el-input>
          </el-col>
        </el-form-item>

        <el-form-item label="内容" prop="resolution">
          <quill-editor
              class="solu-editor"
              ref="resolutionQuill"
              v-model="editForm.resolution"
              :options="resolutionEditorOption"
              @ready="(quill) => (resolutionQuill = quill)"
          ></quill-editor>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="submitForm">保存修改</el-button>
          <el-button @click="handleClose">取消</el-button>
        </el-form-item>
      </el-form>
    </div>
  </el-drawer>
</template>

<script>
import { uploadFile } from "@/api/upload";
import InsertLink from "@/components/quillCom/InsertLink.vue";
import * as Quill from "quill";

// 富文本工具栏配置（复用模板逻辑）
const toolOptions = [
  ["image"],
  ["bold", "italic", "underline", "strike"],
  ["blockquote", "code-block"],
  [{ header: 1 }, { header: 2 }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ script: "sub" }, { script: "super" }],
  [{ indent: "-1" }, { indent: "+1" }],
  [{ direction: "rtl" }],
  [
    {
      size: [
        "10px",
        "12px",
        "14px",
        "16px",
        "18px",
        "20px",
        "22px",
        "24px",
        "26px",
        "28px",
        "30px",
        "32px",
        "36px",
      ],
    },
  ],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }],
  [
    {
      font: ["Microsoft-YaHei", "SimSun", "SimHei", "KaiTi", "FangSong", "Arial", "sans-serif"],
    },
  ],
  [{ align: [] }],
  ["clean"],
  ["link"],
];

export default {
  name: "InfoEditDrawer",
  components: { InsertLink },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    rowData: {
      type: Object,
      default: () => ({}),
    },
    categoryData: {
      type: Array,
      default: () => [],
    },
    systemData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isDestroyed: false,
      editForm: {
        id: "",
        title: "",
        categoryId: "",
        systemId: "",
        rank: "",
        description: "",
        resolution: "",
        keywords: "",
      },
      // 富文本实例
      descriptionQuill: null,
      resolutionQuill: null,
      // 上传相关
      currentEditorType: "",
      currentQuill: null,
      imageUpload: false,
      // 链接插入相关
      insertLinkShow: false,
      insertLinkName: "",
      // 编辑器配置
      descriptionEditorOption: {
        theme: "snow",
        modules: {
          toolbar: {
            container: toolOptions,
            handlers: {
              image: () => this.handleImageUpload("description"),
              link: () => this.handleLinkInsert("description"),
            },
          },
        },
      },
      resolutionEditorOption: {
        theme: "snow",
        modules: {
          toolbar: {
            container: toolOptions,
            handlers: {
              image: () => this.handleImageUpload("resolution"),
              link: () => this.handleLinkInsert("resolution"),
            },
          },
        },
      },
      // 表单验证规则
      rules: {
        title: [
          { required: true, message: "请输入标题", trigger: "blur" },
          { min: 3, max: 30, message: "长度在 3 到 30 个字符", trigger: "blur" },
          { validator: this.validateTitle, trigger: "blur" },
        ],
        resolution: [
          { required: true, message: "请填写问题解答", trigger: "change" },
          { validator: this.validateEditorContent, trigger: "change" },
        ]
      },
    };
  },
  watch: {
    visible(val) {
      if (val && this.rowData) {
        // 回显数据
        this.editForm = {
          ...this.rowData,
        };
        // 富文本内容回显
        this.$nextTick(() => {
          if (this.resolutionQuill && this.rowData.resolution) {
            this.resolutionQuill.clipboard.dangerouslyPasteHTML(this.rowData.resolution);
          }
        });
      }
    },
  },
  methods: {
    // 表单验证
    validateTitle(rule, value, callback) {
      if (/[<>\\/"]/.test(value)) {
        callback(new Error('标题不能包含特殊字符: < > \ / "'));
      } else {
        callback();
      }
    },
    validateEditorContent(rule, value, callback) {
      if (!value || (typeof value === "string" && value.trim() === "")) {
        callback(new Error("内容不能为空"));
      } else {
        callback();
      }
    },

    // 富文本图片上传
    handleImageUpload(type) {
      this.currentEditorType = type;
      this.currentQuill = type === "description" ? this.descriptionQuill : this.resolutionQuill;
      this.imageUpload = true;
      this.$nextTick(() => {
        const input = this.$refs.uploadRef.$el.querySelector("input");
        if (input) input.click();
      });
    },

    // 富文本链接插入
    handleLinkInsert(type) {
      this.currentEditorType = type;
      this.currentQuill = type === "description" ? this.descriptionQuill : this.resolutionQuill;
      const range = this.currentQuill.getSelection();
      if (range && range.length > 0) {
        this.insertLinkName = this.currentQuill.getText(range.index, range.length);
      }
      this.insertLinkShow = true;
      // 阻止事件冒泡到父级抽屉
      event?.stopPropagation();  // 关键：阻止事件冒泡
    },

    // 文件上传处理
    handleFileUpload(file) {
      if (!file.file) return;
      const formData = new FormData();
      formData.append("file", file.file);
      formData.append("returnUrl", this.imageUpload);

      uploadFile(formData) // 调用上传接口
          .then((res) => {
            if (res && res.data) {
              if (this.imageUpload) {
                this.saveImageLink(this.currentQuill, res.data);
              } else {
                this.saveLink(this.currentQuill, res.data, "查看文件", true);
              }
            } else {
              this.$message.error("文件上传失败");
            }
          })
          .catch((err) => {
            console.error("上传失败", err);
            this.$message.error("上传失败，请重试");
          });
    },

    // 保存图片链接
    saveImageLink(quill, url) {
      if (!quill || !url) return;
      const index = quill.getSelection()?.index || quill.getLength();
      quill.insertEmbed(index, "image", url);
      quill.setSelection(index + 1);
    },

    // 保存链接
    saveLink(quill, url, text, isUpload) {
      if (!quill || !url) return;
      text = isUpload ? `附件：${text}` : text;
      const index = quill.getSelection()?.index || quill.getLength();
      quill.insertText(index, text, "link", url);
      quill.setSelection(index + text.length);
    },

    // 确认插入链接
    insertLink(data) {
      if (data && data.url) {
        this.saveLink(this.currentQuill, data.url, data.text || "链接");
      }
      this.insertLinkShow = false;
    },

    // 提交表单
    submitForm() {
      this.$refs.editFormRef.validate(async (valid) => {
        if (valid) {
          if (this.resolutionQuill) {
            this.editForm.resolution = this.resolutionQuill.root.innerHTML;
          }
          this.$emit("save", this.editForm); // 向父组件传递保存的数据
        }
      });
    },

    // 关闭抽屉
    handleClose() {
      this.$refs.editFormRef.resetFields();
      this.insertLinkShow = false;
      this.$emit("close"); // 通知父组件关闭
    },
  },
  beforeDestroy() {
    this.isDestroyed = true;
    // 销毁富文本实例前先检查实例是否存在且destroy方法有效
    if (this.resolutionQuill && typeof this.resolutionQuill.destroy === 'function') {
      this.resolutionQuill.destroy();
    }
    // 清空实例引用
    this.resolutionQuill = null;
  }
};
</script>

<style scoped>
.form-box {
  padding: 0 30px 0 20px;
}
.solu-editor >>> .ql-snow .ql-editor img {
  max-width: 100%; /* 改为100%自适应容器宽度 */
  /* 或设置固定最大宽度：max-width: 800px; */
  height: auto; /* 保持宽高比 */
  image-rendering: -moz-crisp-edges;
  image-rendering: -o-crisp-edges;
  image-rendering: -webkit-crisp-edges;
  image-rendering: crisp-edges;
  -ms-interpolation-mode: nearest-neighbor;
}

.ques-editor >>> .ql-snow .ql-editor img {
  max-width: 100%; /* 同上修改 */
  height: auto;
  image-rendering: -moz-crisp-edges;
  image-rendering: -o-crisp-edges;
  image-rendering: -webkit-crisp-edges;
  image-rendering: crisp-edges;
  -ms-interpolation-mode: nearest-neighbor;
}
/* 移动端适配 - 调整输入框宽度 */
@media screen and (max-width: 768px) {
  /* 标题输入框在移动端占满宽度 */
  .form-box >>> .el-form-item:nth-child(1) .el-col {
    flex: 0 0 100% !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  /* 关键词输入框在移动端占满宽度 */
  .form-box >>> .el-form-item:nth-child(7) .el-col {
    flex: 0 0 100% !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  /* 调整移动端抽屉内边距，增加可用空间 */
  .form-box {
    padding: 0 15px;
  }
}


</style>